import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHead, Btn, Card, Field, MoneyField, Select, Toggle, ImageUpload, FilmUpload, Label, AiCopyButton, AiSeoButton, SeoPreview } from "@/admin/ui";
import { toast } from "sonner";
import { Trash2, Plus, Sparkles, CheckCircle2, Circle } from "lucide-react";

const STATUSES = ["draft", "coming_soon", "early_access", "live", "pre_order", "sold_out", "archived"];
const BADGES = ["NEW", "SIGNATURE", "LIMITED", "RESTOCKED", "COMING SOON", "PRE-ORDER"];

const ProductEditor = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [collections, setCollections] = useState([]);
  const [dirty, setDirty] = useState(false);
  const autoTried = useRef(false);
  const pristine = useRef(true);

  useEffect(() => {
    api.get(`/admin/products/${id}`).then((r) => { setP(r.data); pristine.current = true; }).catch(() => toast.error("Not found"));
    api.get("/admin/collections").then((r) => setCollections(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!p || autoTried.current) return;
    const blank = !p.short_description && !p.long_description && !p.care;
    if (p.name && p.name !== "Untitled Piece" && blank) {
      autoTried.current = true;
      api.post("/admin/ai/product-fill", { name: p.name, category: p.category, colours: (p.colors || []).map((c) => c.name), department: p.department })
        .then(({ data }) => {
          setP((s) => ({ ...s,
            short_description: s.short_description || data.short_description,
            long_description: s.long_description || data.long_description,
            care: s.care || data.care,
            seo: { ...s.seo, title: s.seo?.title || data.seo?.title, description: s.seo?.description || data.seo?.description } }));
          toast("Draft copy prepared — review & save");
        }).catch(() => {});
    }
  }, [p]);

  // Mark the editor dirty on any change after the initial load / AI draft.
  useEffect(() => {
    if (!p) return;
    if (pristine.current) { pristine.current = false; return; }
    setDirty(true);
  }, [p]);

  // Warn before leaving/reloading the tab with unsaved edits.
  useEffect(() => {
    window.__nbaEditorDirty = dirty;
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => { window.removeEventListener("beforeunload", h); window.__nbaEditorDirty = false; };
  }, [dirty]);

  if (!p) return <p className="text-espresso/50">Loading…</p>;
  const up = (k, v) => setP((s) => ({ ...s, [k]: v }));

  const save = async () => {
    try { await api.put(`/admin/products/${id}`, p); toast.success("Saved"); setDirty(false); }
    catch { toast.error("Save failed"); }
  };

  const goBack = () => { if (dirty && !window.confirm("You have unsaved changes. Leave without saving?")) return; nav("/admin/products"); };
  const preview = async () => { if (dirty) await save(); window.open(`/products/${p.slug}`, "_blank", "noreferrer"); };

  const previewDrop = async () => {
    await save();
    window.open(`/products/${p.slug}`, "_blank", "noreferrer");
  };

  const fillFromPhoto = async (urlArg, auto = false) => {
    const url = (typeof urlArg === "string" && urlArg) || (p.colors || []).flatMap((c) => [c.main_image, c.front_image, c.back_image, c.lifestyle_image]).filter(Boolean)[0];
    if (!url) { toast.error("Add a product photo first, then draft from it."); return; }
    let hints = "";
    if (!auto) { hints = window.prompt("Anything you know for certain? (fabric, story — optional; leave blank to let the assistant read the photo)", ""); if (hints === null) return; }
    try {
      toast("Reading the photo & drafting…");
      const { data } = await api.post("/admin/ai/product-from-photo", { image_url: url, name: p.name, department: p.department, hints });
      const uniq = (data.unique_points || []).length ? `\n\nSignature details:\n• ${data.unique_points.join("\n• ")}` : "";
      setP((s) => ({ ...s,
        name: (!s.name || s.name === "Untitled Piece") && data.suggested_name ? data.suggested_name : s.name,
        category: s.category || data.category || s.category,
        short_description: data.short_description || s.short_description,
        long_description: (data.long_description || s.long_description || "") + uniq,
        details: data.details || s.details,
        fit: data.fit || s.fit,
        composition: data.composition || s.composition,
        care: data.care || s.care,
        seo: { ...s.seo, title: data.seo?.title || s.seo?.title, description: data.seo?.description || s.seo?.description },
        instagram_caption: data.instagram_caption || s.instagram_caption }));
      toast.success("Drafted from photo — review & edit");
      if (data.suggested_price_range) toast(`Price idea to consider: ${data.suggested_price_range} (guidance only — you set the price)`, { duration: 8000 });
    } catch { toast.error("Could not read that photo"); }
  };

  const fillAI = async () => {
    const details = window.prompt("Any key details to weave in? (fabric, fit, story — optional)", "");
    if (details === null) return;
    try {
      toast("Drafting from details…");
      const { data } = await api.post("/admin/ai/product-fill", { name: p.name, category: p.category, colours: (p.colors || []).map((c) => c.name), details, department: p.department });
      setP((s) => ({ ...s,
        short_description: data.short_description || s.short_description,
        long_description: data.long_description || s.long_description,
        details: data.details || s.details,
        fit: data.fit || s.fit,
        composition: data.composition || s.composition,
        care: data.care || s.care,
        seo: { ...s.seo, title: data.seo?.title || s.seo?.title, description: data.seo?.description || s.seo?.description } }));
      toast.success("Drafted — review & edit");
    } catch { toast.error("Could not draft"); }
  };

  // colours
  const addColour = () => setP((s) => ({ ...s, colors: [...(s.colors || []), { name: "New Colour", family: "", swatch: "#cccccc", main_image: "", front_image: "", back_image: "", lifestyle_image: "", images: [], sizes: [] }] }));
  const upColour = (i, k, v) => setP((s) => { const c = [...s.colors]; c[i] = { ...c[i], [k]: v }; return { ...s, colors: c }; });
  const addImage = (ci) => setP((s) => { const c = [...s.colors]; c[ci] = { ...c[ci], images: [...(c[ci].images || []), ""] }; return { ...s, colors: c }; });
  const setImage = (ci, idx, v) => setP((s) => { const c = [...s.colors]; const imgs = [...(c[ci].images || [])]; imgs[idx] = v; c[ci] = { ...c[ci], images: imgs }; return { ...s, colors: c }; });
  const removeImage = (ci, idx) => setP((s) => { const c = [...s.colors]; c[ci] = { ...c[ci], images: (c[ci].images || []).filter((_, x) => x !== idx) }; return { ...s, colors: c }; });
  const delColour = (i) => setP((s) => ({ ...s, colors: s.colors.filter((_, x) => x !== i) }));
  const addSize = (ci) => setP((s) => { const c = [...s.colors]; c[ci] = { ...c[ci], sizes: [...(c[ci].sizes || []), { size: "M", sku: "", stock: 0, low_stock_threshold: 3 }] }; return { ...s, colors: c }; });
  const upSize = (ci, si, k, v) => setP((s) => { const c = [...s.colors]; const sizes = [...c[ci].sizes]; sizes[si] = { ...sizes[si], [k]: k === "size" || k === "sku" ? v : Number(v) }; c[ci] = { ...c[ci], sizes }; return { ...s, colors: c }; });
  const delSize = (ci, si) => setP((s) => { const c = [...s.colors]; c[ci] = { ...c[ci], sizes: c[ci].sizes.filter((_, x) => x !== si) }; return { ...s, colors: c }; });
  const toggleBadge = (b) => setP((s) => ({ ...s, badges: (s.badges || []).includes(b) ? s.badges.filter((x) => x !== b) : [...(s.badges || []), b] }));

  const colOpts = [{ value: "", label: "— none —" }, ...collections.map((c) => ({ value: c.slug, label: c.name }))];

  const _stock = (p.colors || []).reduce((n, c) => n + (c.sizes || []).reduce((m, s) => m + (Number(s.stock) || 0), 0), 0);
  const checklist = [
    { key: "price", ok: p.price != null, label: "Price set" },
    { key: "stock", ok: _stock > 0, label: "Stock added" },
    { key: "photos", ok: (p.colors || []).some((c) => c.lifestyle_image || c.main_image || (c.images || []).length), label: "Photos added" },
    { key: "copy", ok: !!p.short_description, label: "Copy written" },
  ];

  return (
    <div className="max-w-3xl">
      <PageHead title={p.name || "Product"} sub={`/products/${p.slug}`}>
        <Btn variant="ghost" onClick={goBack} testid="product-back">Back</Btn>
        <Btn variant="ghost" onClick={preview} testid="product-preview">Preview</Btn>
        {(p.drop_at || p.status === "coming_soon") && (
          <Btn variant="ghost" onClick={previewDrop} testid="preview-drop">Preview drop →</Btn>
        )}
        <Btn variant="ghost" onClick={fillFromPhoto} testid="product-ai-photo"><Sparkles size={13} className="inline" /> Draft from photo</Btn>
        <Btn variant="ghost" onClick={fillAI} testid="product-ai-fill"><Sparkles size={13} className="inline" /> Fill from details</Btn>
        {dirty && <span className="text-[0.6rem] tracking-[0.18em] uppercase text-wine self-center" data-testid="unsaved-indicator">Unsaved</span>}
        <Btn onClick={save} testid="admin-save">{dirty ? "Save changes" : "Save"}</Btn>
      </PageHead>

      <Card className="mb-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <p className="overline text-espresso/55">Ready to sell</p>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {checklist.map((c) => (
              <span key={c.key} data-testid={`ready-${c.key}`} className={`flex items-center gap-1.5 text-xs ${c.ok ? "text-sage" : "text-espresso/40"}`}>
                {c.ok ? <CheckCircle2 size={15} strokeWidth={1.6} /> : <Circle size={15} strokeWidth={1.4} />} {c.label}
              </span>
            ))}
          </div>
        </div>
        {p.status === "live" && checklist.some((c) => !c.ok) && (
          <p className="text-xs text-wine mt-3" data-testid="ready-warning">This piece is live but shoppers can't fully buy it yet — complete the items above.</p>
        )}
      </Card>

      <Card className="mb-4">
        <div className="grid md:grid-cols-2 gap-x-5">
          <Field label="Product name" value={p.name} onChange={(v) => up("name", v)} />
          <Field label="Slug" value={p.slug} onChange={(v) => up("slug", v)} />
          <Select label="Status" value={p.status} onChange={(v) => up("status", v)} options={STATUSES} />
          <Select label="Department" value={p.department} onChange={(v) => up("department", v)} options={["women", "men"]} />
          <Field label="Category" value={p.category} onChange={(v) => up("category", v)} />
          <Select label="Collection" value={p.collection_slug} onChange={(v) => up("collection_slug", v)} options={colOpts} />
          <Field label="Drop slug" value={p.drop_slug} onChange={(v) => up("drop_slug", v)} />
          <Field label="Drop date & time (for 'Coming soon' countdown)" type="datetime-local" value={p.drop_at} onChange={(v) => up("drop_at", v)} testid="product-drop-at" />
          <MoneyField label="Price (£)" value={p.price} onChange={(v) => up("price", v)} placeholder="leave blank = Price on request" testid="product-price" />
          <MoneyField label="Compare-at price (£)" value={p.compare_at_price} onChange={(v) => up("compare_at_price", v)} testid="product-compare-price" />
          <Field label="Sort order" type="number" value={p.sort_order} onChange={(v) => up("sort_order", v)} />
        </div>
        <div className="mb-4">
          <Label>Badges</Label>
          <div className="flex flex-wrap gap-2">
            {BADGES.map((b) => (
              <button key={b} type="button" onClick={() => toggleBadge(b)}
                className={`text-[0.62rem] tracking-[0.12em] uppercase px-2.5 py-1.5 border ${(p.badges || []).includes(b) ? "bg-espresso text-cream border-espresso" : "border-espresso/25"}`}>{b}</button>
            ))}
          </div>
        </div>
        <Field label="Short description" value={p.short_description} onChange={(v) => up("short_description", v)} />
        <AiCopyButton instruction={`Write a single-line short description for "${p.name}" (${p.category || "piece"}). Key details: `} current={p.short_description} onResult={(v) => up("short_description", v)} testid="product-ai-short" />
        <Field label="Long description / story" textarea value={p.long_description} onChange={(v) => up("long_description", v)} />
        <AiCopyButton instruction={`Write the long product description and story for "${p.name}" (${p.category || "piece"}) in the brand voice. Key details: `} current={p.long_description} onResult={(v) => up("long_description", v)} testid="product-ai-long" />
        <div className="grid md:grid-cols-2 gap-x-5">
          <Field label="The Details" textarea value={p.details} onChange={(v) => up("details", v)} />
          <Field label="Fit & Sizing" textarea value={p.fit} onChange={(v) => up("fit", v)} />
          <Field label="Composition" textarea value={p.composition} onChange={(v) => up("composition", v)} />
          <Field label="Care" textarea value={p.care} onChange={(v) => up("care", v)} />
        </div>
        <AiCopyButton instruction={`Write care notes for "${p.name}". Only use materials/instructions I provide here — write [to confirm] for anything not given. Key details: `} current={p.care} onResult={(v) => up("care", v)} testid="product-ai-care" />
        <div className="mt-3">
          <Label>Fit note (shown near sizes)</Label>
          <input value={p.fit_note || ""} onChange={(e) => up("fit_note", e.target.value)} data-testid="product-fit-note"
            placeholder="e.g. True to size · Model wears a size S" className="w-full bg-ivory border border-espresso/20 px-3 py-2 text-sm" />
        </div>
        <div className="grid md:grid-cols-2 gap-x-5 mt-4">
          <FilmUpload label="Opening film (detail video)" value={p.film} onChange={(v) => up("film", v)} testid="product-film" />
          <ImageUpload label="Film poster (still shown before it plays)" value={p.film_poster} onChange={(v) => up("film_poster", v)} />
        </div>
        <ImageUpload label="Editorial image (pinned beside the product — a scene/still, not on-body)" value={p.editorial_image} onChange={(v) => up("editorial_image", v)} />
      </Card>

      {/* Variants */}
      <Card className="mb-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-serif text-2xl">Colours & Inventory</h2>
          <Btn variant="ghost" onClick={addColour} testid="admin-add-colour"><Plus size={13} className="inline" /> Colour</Btn>
        </div>
        {(p.colors || []).map((c, ci) => (
          <div key={ci} className="border border-espresso/15 p-4 mb-4">
            <div className="flex items-center gap-3 mb-3">
              <input type="color" value={c.swatch || "#cccccc"} onChange={(e) => upColour(ci, "swatch", e.target.value)} className="w-9 h-9 border border-espresso/20" />
              <input value={c.name} onChange={(e) => upColour(ci, "name", e.target.value)} placeholder="Colour name"
                className="flex-1 bg-ivory border border-espresso/20 px-3 py-2 text-sm" />
              <input value={c.family} onChange={(e) => upColour(ci, "family", e.target.value)} placeholder="Colour family"
                className="flex-1 bg-ivory border border-espresso/20 px-3 py-2 text-sm" />
              <button onClick={() => delColour(ci)} className="text-wine"><Trash2 size={16} /></button>
            </div>
            <ImageUpload label="Lifestyle (hero — the big picture)" value={c.lifestyle_image} onChange={(v) => upColour(ci, "lifestyle_image", v)} onDraft={(url) => fillFromPhoto(url, true)} testid={`colour-${ci}-lifestyle-image`} />
            <div className="flex justify-between items-center mb-2 mt-2">
              <p className="overline text-espresso/55">Product images (front, back, model shots — shown on the page)</p>
              <Btn variant="ghost" onClick={() => addImage(ci)} testid={`colour-${ci}-add-image`}><Plus size={13} className="inline" /> Add image</Btn>
            </div>
            <div className="grid md:grid-cols-2 gap-x-4">
              {(c.images || []).map((img, ii) => (
                <div key={ii} className="relative">
                  <ImageUpload label={`Image ${ii + 1}`} value={img} onChange={(v) => setImage(ci, ii, v)} testid={`colour-${ci}-image-${ii}`} />
                  <button type="button" onClick={() => removeImage(ci, ii)} data-testid={`colour-${ci}-image-${ii}-remove`} className="absolute -top-1 right-0 text-wine text-[0.58rem] tracking-widest uppercase">Remove</button>
                </div>
              ))}
            </div>
            <div className="mt-2">
              <div className="flex justify-between items-center mb-2">
                <Label>Sizes & Stock</Label>
                <button onClick={() => addSize(ci)} className="text-[0.64rem] tracking-widest uppercase border border-espresso px-2 py-1"><Plus size={11} className="inline" /> Size</button>
              </div>
              {(c.sizes || []).map((s, si) => (
                <div key={si} className="flex items-center gap-2 mb-2">
                  <input value={s.size} onChange={(e) => upSize(ci, si, "size", e.target.value)} placeholder="Size" className="w-20 bg-ivory border border-espresso/20 px-2 py-1.5 text-sm" />
                  <input value={s.sku} onChange={(e) => upSize(ci, si, "sku", e.target.value)} placeholder="SKU" className="flex-1 bg-ivory border border-espresso/20 px-2 py-1.5 text-sm" />
                  <input type="number" value={s.stock} onChange={(e) => upSize(ci, si, "stock", e.target.value)} placeholder="Stock" title="Stock" className="w-20 bg-ivory border border-espresso/20 px-2 py-1.5 text-sm" />
                  <input type="number" value={s.low_stock_threshold} onChange={(e) => upSize(ci, si, "low_stock_threshold", e.target.value)} title="Low stock threshold" className="w-16 bg-ivory border border-espresso/20 px-2 py-1.5 text-sm" />
                  <button onClick={() => delSize(ci, si)} className="text-wine"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </Card>

      <Card>
        <h2 className="font-serif text-2xl mb-4">SEO & Related</h2>
        <AiSeoButton subject={p.name} context={p.long_description || p.short_description} department={p.department} onResult={(d) => up("seo", { ...p.seo, title: d.title, description: d.description })} testid="product-ai-seo" />
        <Field label="SEO Title" value={p.seo?.title} onChange={(v) => up("seo", { ...p.seo, title: v })} />
        <Field label="Meta Description" textarea value={p.seo?.description} onChange={(v) => up("seo", { ...p.seo, description: v })} />
        <SeoPreview title={p.seo?.title} description={p.seo?.description} path={`/products/${p.slug}`} />
        <ImageUpload label="OG / Social image" value={p.seo?.og_image} onChange={(v) => up("seo", { ...p.seo, og_image: v })} />
        <Field label="Related product IDs (comma separated)" value={(p.related_product_ids || []).join(",")} onChange={(v) => up("related_product_ids", v.split(",").map((x) => x.trim()).filter(Boolean))} />
      </Card>
    </div>
  );
};

export default ProductEditor;
